import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavbarButton from "./components/NavbarButton";
import PlusCircleIcon from "@/assets/icons/plus-circle.svg?react";
import PlusCircleOutlineIcon from "@/assets/icons/plus-circle-outline.svg?react";
import ClockIcon from "@/assets/icons/clock.svg?react";
import ClockOutlineIcon from "@/assets/icons/clock-outline.svg?react";
import CheckCircleIcon from "@/assets/icons/check-circle.svg?react";
import CheckCircleOutlineIcon from "@/assets/icons/check-circle-outline.svg?react";
import AccountCircleIcon from "@/assets/icons/user-circle.svg?react";
import AccountCircleOutlineIcon from "@/assets/icons/user-circle-outline.svg?react";
import LogOutIcon from "@/assets/icons/log-out-03.svg?react";
import FolderIcon from "@/assets/icons/folder.svg?react";
import FolderOutlineIcon from "@/assets/icons/folder-outline.svg?react";
import Paths from "@/constants/paths";
import clsx from "clsx";
import MenuIcon from "@/assets/icons/menu-01.svg?react";
import CloseIcon from "@/assets/icons/x.svg?react";
import PelicanLogo from "@/assets/images/logo.png";
import { useEffect, useState } from "react";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useQueryClient } from "@tanstack/react-query";
import PhoneIcon from "@/assets/icons/phone.svg?react";
import MailIcon from "@/assets/icons/mail-01.svg?react";
import { useGetCompany } from "@/api/company.api";
import { NavLinkBtn } from "./components/navlinkBtn";
import { roleCheck } from "@/lib/roleCheck";
import { Tooltip } from "react-tooltip";
import { useGetInvoiceCountV1, useGetLitigeCountV1 } from "@/api/invoice";
import { logout } from "@/stores/authStore";
import { useGetExpeditionCount } from "@/api/expedition.api";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";

const Separator = () => (
  <div className="my-3 h-px w-full self-center bg-gray-300 md:my-0 md:w-3/4" />
);

type NavbarProps = {
  role?: string;
};

const Navbar = ({ role }: NavbarProps) => {
  const { t } = useTranslation("shared");
  const [isNavOpen, setIsNavOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: company } = useGetCompany();
  const { data: expeditionCount } = useGetExpeditionCount({
    processStatus: [
      ExpeditionProcessStatusEnum.PENDING_QUOTE,
      ExpeditionProcessStatusEnum.PENDING_CUSTOMER,
      ExpeditionProcessStatusEnum.PENDING_QUOTE_ACCEPTATION,
    ],
  });

  const { data: otherInvoiceCount } = useGetLitigeCountV1({
    paymentStatus: "TOBEPAID",
  });
  const { data: invoicesCount } = useGetInvoiceCountV1({
    paymentStatus: "TOBEPAID",
  });

  const isLarge = useMediaQuery("(min-width: 768px)");
  const iconSize = isLarge ? 32 : 24;

  const location = useLocation();

  useEffect(() => {
    setIsNavOpen(false);
    queryClient.invalidateQueries({ queryKey: ["cotationsCount"] });
    queryClient.invalidateQueries({ queryKey: ["customCotationsCount"] });
  }, [location, queryClient]);

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isNavOpen]);

  const Buttons = () => (
    <div className="scrollbar flex flex-1 flex-col justify-between overflow-x-hidden overflow-y-visible">
      <div className="flex flex-1 flex-col gap-0 md:gap-1">
        {roleCheck(["ADMIN", "SUPERADMIN", "MEMBER"], role) && (
          <>
            <NavLinkBtn
              path={Paths.home.cotations}
              iconSize={iconSize}
              Icon={PlusCircleIcon}
              IconOutline={PlusCircleOutlineIcon}
              title={t("clientNavbar.newQuotation")}
            />
            <NavLink to={Paths.home.pendingShipments}>
              {({ isActive }) => (
                <div className="relative">
                  <NavbarButton
                    icon={
                      <div className="relative">
                        {isActive ? (
                          <ClockIcon width={iconSize} height={iconSize} />
                        ) : (
                          <ClockOutlineIcon width={iconSize} height={iconSize} />
                        )}
                        {expeditionCount && expeditionCount > 0 ? (
                          <div className="center absolute top-[-5px] right-[-5px] z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-700 p-2.5 text-white">
                            <div className="font-semibold text-white">{expeditionCount}</div>
                          </div>
                        ) : null}
                      </div>
                    }
                    title={t("clientNavbar.pendingShipments")}
                    isActive={isActive}
                  />
                </div>
              )}
            </NavLink>

            <NavLink to={Paths.home.validatedShipments}>
              {({ isActive }) => (
                <div className="relative">
                  <NavbarButton
                    icon={
                      <div className="relative">
                        {isActive ? (
                          <CheckCircleIcon width={iconSize} height={iconSize} />
                        ) : (
                          <CheckCircleOutlineIcon width={iconSize} height={iconSize} />
                        )}
                      </div>
                    }
                    title={t("clientNavbar.validatedShipments")}
                    isActive={isActive}
                  />
                </div>
              )}
            </NavLink>
          </>
        )}

        {roleCheck(["ADMIN", "SUPERADMIN", "ACCOUNTANT"], role) && (
          <>
            <Separator />
            {roleCheck(["ADMIN", "SUPERADMIN"], role) && (
              <>
                <NavLinkBtn
                  path={Paths.home.profile}
                  iconSize={iconSize}
                  Icon={AccountCircleIcon}
                  IconOutline={AccountCircleOutlineIcon}
                  title={t("clientNavbar.profile")}
                />
              </>
            )}

            <NavLink to={Paths.home.invoices}>
              {({ isActive }) => (
                <NavbarButton
                  icon={
                    <div className="relative">
                      {isActive ? (
                        <FolderIcon width={iconSize} height={iconSize} />
                      ) : (
                        <FolderOutlineIcon width={iconSize} height={iconSize} />
                      )}
                      {(otherInvoiceCount || 0) + (invoicesCount || 0) > 0 ? (
                        <div className="center absolute top-[-5px] right-[-5px] z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-700 p-2.5 text-white">
                          <div className="font-semibold text-white">
                            {(otherInvoiceCount || 0) + (invoicesCount || 0)}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  }
                  title={t("clientNavbar.invoices")}
                  isActive={isActive}
                />
              )}
            </NavLink>
          </>
        )}

        <Separator />
        <div className="z-50 hidden flex-1 flex-col gap-0 md:flex">
          <a
            href={`mailto:${company?.contactEmail || "assistance@pelicanairservices.com"}`}
            target="_blank"
            className="group relative z-50 flex h-[60px] w-full flex-row items-center justify-center md:h-[74px]"
            data-tooltip-id="email-tooltip"
            data-tooltip-content={company?.contactEmail || "assistance@pelicanairservices.com"}
          >
            <MailIcon width={iconSize} height={iconSize} />
            <Tooltip id="email-tooltip" positionStrategy="fixed" />
          </a>

          <a
            href={`tel: ${company?.contactPhoneNumber || "+33972528537"}`}
            target="_blank"
            className="group relative z-50 flex h-[60px] w-full flex-row items-center justify-center md:h-[74px]"
            data-tooltip-id="phone-tooltip"
            data-tooltip-content={company?.contactPhoneNumber || "+33972528537"}
          >
            <PhoneIcon width={iconSize} height={iconSize} />
            <Tooltip id="phone-tooltip" positionStrategy="fixed" />
          </a>
        </div>
        <div className="flex flex-1 flex-col md:hidden">
          <a
            href={`mailto:${company?.contactEmail || "assistance@pelicanairservices.com"}`}
            target="_blank"
            className="flex h-[56px] w-full flex-row items-center justify-start gap-1 p-4"
          >
            <MailIcon width={iconSize} height={iconSize} />
            <p className="text-sm text-gray-600">
              {company?.contactEmail || "assistance@pelicanairservices.com"}
            </p>
          </a>

          <a
            target="_blank"
            href={`tel: ${company?.contactPhoneNumber || "+33972528537"}`}
            className="flex h-[56px] w-full flex-row items-center justify-start gap-1 p-4"
          >
            <PhoneIcon width={iconSize} height={iconSize} />
            <p className="text-sm text-gray-600">{company?.contactPhoneNumber || "+33972528537"}</p>
          </a>
        </div>
      </div>

      <button className="mt-auto flex flex-col items-center" onClick={() => logout()}>
        <div className="h-px w-full self-center bg-gray-100" />
        <NavbarButton
          icon={<LogOutIcon width={iconSize} height={iconSize} />}
          title={t("clientNavbar.logout")}
        />
      </button>
    </div>
  );

  return (
    <div className="flex flex-col shadow-lg md:h-screen md:flex-row">
      <div
        className={clsx([
          "fixed top-0 right-0 bottom-0 left-0 z-100 h-dvh bg-[#00000080] md:hidden",
          { invisible: !isNavOpen },
        ])}
        onClick={() => setIsNavOpen(false)}
      />
      <div
        className={clsx([
          "fixed top-0 right-0 bottom-0 left-8 z-100 h-dvh bg-white md:hidden",
          {
            invisible: !isNavOpen,
            "left-8": isNavOpen,
            "w-0": !isNavOpen,
          },
        ])}
      >
        <div className="flex h-full flex-1 flex-col">
          <div className="mx-4 my-6 flex flex-row items-center justify-between md:hidden">
            <img src={PelicanLogo} className="h-11" />
            <CloseIcon className="text-gray-600" onClick={() => setIsNavOpen(false)} />
          </div>
          <Separator />
          <Buttons />
        </div>
      </div>
      <div className="bn mx-4 my-6 flex flex-row items-center justify-between md:hidden">
        <img src={PelicanLogo} className="h-11" />
        <MenuIcon className="text-gray-600" onClick={() => setIsNavOpen(true)} />
      </div>
      <div className="hidden h-full max-w-32 flex-col gap-6 border-r-2 border-gray-100 bg-white md:flex">
        <button onClick={() => navigate(Paths.home.cotations)} className="focus:outline-hidden">
          <img src="/images/pelican-logo.png" className="w-full self-center p-4" />
        </button>
        <Buttons />
      </div>
    </div>
  );
};

export default Navbar;
